import React, { useRef, useEffect, useState } from 'react';
import logger from '../utils/logger';
import './QuestionResult.css';

const QuestionResult = ({ 
  question, 
  userAnswer, 
  shuffledChoices, 
  onNext, 
  isLastQuestion,
  questionNumber,
  totalQuestions,
  onBack
}) => {
  const correctChoices = question.choices.filter(choice => choice.isCorrect);
  const isCorrect = userAnswer.isCorrect;
  const questionStartRef = useRef(null);
  const resultStatusRef = useRef(null);
  const [isExplanationExpanded, setIsExplanationExpanded] = useState(false);

  useEffect(() => {
    // Scroll to result status when component mounts (after answering)
    setTimeout(() => {
      resultStatusRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleNext = () => {
    logger.userAction('view_next_after_explanation', {
      questionId: question.id,
      questionNumber,
      isCorrect,
      isLastQuestion
    });
    onNext();
  };

  // Check if explanation should be collapsible (200+ characters)
  const shouldCollapseExplanation = (explanation) => {
    if (!explanation) return false;
    return explanation.length >= 200;
  };

  const toggleExplanation = () => {
    setIsExplanationExpanded(!isExplanationExpanded);
  };

  const getChoiceStatus = (choice) => {
    const isSelected = userAnswer.selectedChoiceIds.includes(choice.id);
    const isCorrectChoice = choice.isCorrect;
    
    if (isSelected && isCorrectChoice) return 'correct-selected';
    if (isSelected && !isCorrectChoice) return 'incorrect-selected';
    if (!isSelected && isCorrectChoice) return 'correct-not-selected';
    return 'not-selected';
  };

  const getChoiceIcon = (choice) => {
    const status = getChoiceStatus(choice);
    switch (status) {
      case 'correct-selected': return '✅';
      case 'incorrect-selected': return '❌';
      case 'correct-not-selected': return '✓';
      default: return '';
    }
  };

  return (
    <div className="question-result">
      <div className="result-header" ref={questionStartRef}>
        <div className="progress-info">
          問題 {questionNumber} / {totalQuestions}
        </div>
        {onBack && (
          <button 
            onClick={onBack}
            className="back-to-list-btn"
            title="クイズ一覧に戻る"
          >
            一覧に戻る
          </button>
        )}
        <div className={`result-status ${isCorrect ? 'correct' : 'incorrect'}`} ref={resultStatusRef}>
          {isCorrect ? '🎉 正解！' : '😞 不正解'}
        </div>
      </div>

      <div className="question-content">
        <h3 className="question-text">{question.questionText}</h3>
        
        {question.questionImageUrl && (
          <img 
            src={question.questionImageUrl} 
            alt="Question" 
            className="question-image"
          />
        )}
        
        {question.tags && question.tags.length > 0 && (
          <div className="question-tags">
            {question.tags.map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>
        )}
      </div>

      <div className="choices-review">
        <h4>回答結果</h4>
        <div className="choices">
          {shuffledChoices.map((choice) => (
            <div
              key={choice.id}
              className={`choice-result ${getChoiceStatus(choice)}`}
            >
              <div className="choice-icon">
                {getChoiceIcon(choice)}
              </div>
              
              <div className="choice-content">
                <span className="choice-text">{choice.text}</span>
                {choice.choiceImageUrl && (
                  <img 
                    src={choice.choiceImageUrl} 
                    alt={`Choice ${choice.id}`} 
                    className="choice-image"
                  />
                )}
              </div>
              
              <div className="choice-status-text">
                {getChoiceStatus(choice) === 'correct-selected' && (
                  <span className="status-label correct">あなたの回答 (正解)</span>
                )}
                {getChoiceStatus(choice) === 'incorrect-selected' && (
                  <span className="status-label incorrect">あなたの回答 (不正解)</span>
                )}
                {getChoiceStatus(choice) === 'correct-not-selected' && (
                  <span className="status-label missed">正解</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {question.explanation && (
        <div className="explanation">
          <div className="explanation-header">
            <h4>解説</h4>
            {shouldCollapseExplanation(question.explanation) && (
              <button 
                onClick={toggleExplanation}
                className="explanation-toggle"
              >
                {isExplanationExpanded ? '▼ 折りたたむ' : '▶ 展開する'}
              </button>
            )}
          </div>
          <div className={`explanation-content ${shouldCollapseExplanation(question.explanation) && !isExplanationExpanded ? 'collapsed' : ''}`}>
            <p>{question.explanation}</p>
            {question.explanationImageUrl && (
              <img 
                src={question.explanationImageUrl} 
                alt="解説図" 
                className="explanation-image"
              />
            )}
          </div>
        </div>
      )}


      <div className="result-navigation">
        <button 
          onClick={handleNext}
          className="next-btn"
        >
          {isLastQuestion ? '結果を見る' : '次の問題へ'}
        </button>
      </div>
    </div>
  );
};

export default QuestionResult;